import { memo, useCallback, useRef, useState, type JSX } from 'react'

import { ImageBlob } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { Table as ReactTableType } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  useColumns,
  NewDialog,
  RowActions,
  useAsyncContentCaching,
  SuccessPanelActions,
} from 'domains/Workspaces/Images/Image/Blobs'
import { imageBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Blobs/Routing'
import { imageObjectQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const routeParams = imageBlobsOverviewRoute.useParams()

  const tableInstance = useRef<ReactTableType<ImageBlob>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const { workspaceQuery, imageBlobsQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: ImageBlob) => {
      enqueueSnackbar(`${t('domain:Workspace.Image.imageCreated', { name: data.storeId })}`, {
        description: `${t('domain:Workspace.Image.imageCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  return (
    <>
      <ActionStrip>
        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.blobBatchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: imageObjectQueryKeys.blobList({
                imageId: routeParams.imageId,
              }).queryKey,
            })
          }, [queryClient, setBatchGetEnabled, routeParams.imageId])}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={imageBlobsQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || imageBlobsQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableInfinity
        infinityProps={pick(imageBlobsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.storeId ?? '',
          entity: t('entity:content.image', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (image) => image.id,
        }}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
